import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import AssigneeCombobox from "@/components/AssigneeCombobox";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarIcon, ArrowLeft } from "lucide-react";
import { formatDate } from "@/lib/statusColor";

export default function CreateTask() {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [assigneeName, setAssigneeName] = useState("");
  const [assigneeEmail, setAssigneeEmail] = useState("");
  const [deadlineDate, setDeadlineDate] = useState();
  const [deadlineTime, setDeadlineTime] = useState("17:00");
  const [priority, setPriority] = useState("none");
  const [tagsText, setTagsText] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!deadlineDate) {
      toast.error("Please pick a deadline date");
      return;
    }
    const [hh, mm] = deadlineTime.split(":");
    const dt = new Date(deadlineDate);
    dt.setHours(parseInt(hh, 10) || 17, parseInt(mm, 10) || 0, 0, 0);

    setBusy(true);
    try {
      const { data } = await api.post("/tasks", {
        title,
        description,
        assignee_name: assigneeName,
        assignee_email: assigneeEmail,
        deadline: dt.toISOString(),
        priority: priority === "none" ? null : priority,
        tags: tagsText.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Task created");
      navigate(`/tasks/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-2xl mx-auto px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4" data-testid="back-to-dashboard-link">
          <ArrowLeft size={14} /> Back to dashboard
        </Link>
        <Card className="rounded-md border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-2xl" style={{ fontFamily: "Outfit, sans-serif" }}>
              New Task
            </CardTitle>
            <p className="text-sm text-slate-500">Assign work with a clear deadline.</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-5">
              <div className="space-y-1.5">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  required
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Draft Q1 report"
                  data-testid="task-title-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Add details, links, or context…"
                  data-testid="task-description-input"
                />
              </div>
              <AssigneeCombobox
                name={assigneeName}
                email={assigneeEmail}
                onChange={(n, em) => {
                  setAssigneeName(n);
                  setAssigneeEmail(em);
                }}
                nameTestId="task-assignee-name-input"
                emailTestId="task-assignee-email-input"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Deadline date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start font-normal"
                        type="button"
                        data-testid="task-deadline-date-btn"
                      >
                        <CalendarIcon size={14} className="mr-2" />
                        {deadlineDate ? formatDate(deadlineDate).split(",")[0] : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={deadlineDate}
                        onSelect={setDeadlineDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="time">Deadline time</Label>
                  <Input
                    id="time"
                    type="time"
                    value={deadlineTime}
                    onChange={(e) => setDeadlineTime(e.target.value)}
                    data-testid="task-deadline-time-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Priority</Label>
                  <Select value={priority} onValueChange={setPriority}>
                    <SelectTrigger className="h-10" data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="tags">Tags <span className="text-xs text-slate-400 font-normal">(comma-separated)</span></Label>
                  <Input id="tags" value={tagsText} onChange={(e) => setTagsText(e.target.value)} placeholder="e.g., invoice, urgent, client-A" data-testid="task-tags-input" />
                </div>
              </div>
              <div className="flex items-center gap-3 pt-2">
                <Button type="submit" disabled={busy} data-testid="task-submit-btn">
                  {busy ? "Creating…" : "Create task"}
                </Button>
                <Link to="/">
                  <Button type="button" variant="outline">
                    Cancel
                  </Button>
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
